import useSalon from '@admin/models/Salon'
import Button from '@blocks/Button'
import Flex from '@blocks/Flex'
import Grid from '@blocks/Grid'
import NavigationLow from '@blocks/NavigationLow'
import { route } from '@helpers'
import useTitle from '@hooks/useTitle'
import { ReactComponent as DeleteIcon } from '@icons/delete.svg'
import { ReactComponent as EditIcon } from '@icons/edit.svg'
import { ReactComponent as EyeIcon } from '@icons/eye.svg'
import { tablet } from '@styles/mixins'
import { grid } from '@styles/vars'
import MainContainer from '@blocks/MainContainer'
import useMainTemplate from '@templates/Main/hooks/useMainTemplate'
import React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled, { css } from 'styled-components'
import SalonManagerContext from './context'
import useSalonManagerHeading from './hooks/useSalonManagerHeading'

export default function SalonManagerTemplate({ children }: React.PropsWithChildren<{}>) {
    const { id } = useParams<{ id: string }>()
    const history = useHistory()

    const salon = useSalon(id)
    const heading = useSalonManagerHeading(salon)

    useTitle(`Manage: ${salon.attrs.name()}`)
    useMainTemplate({ category: "salons" })

    const deleteSalon = async () => {
        await salon.actions.destroy()
        history.push(route("admin:salons"))
    }

    if (!salon.state) 
        return null

    return (
        <SalonManagerContext.Provider value={{ salon } as Admin.Contexts.SalonManager}>
            <MainContainer heading={heading}>
                <Grid>
                    <ActionsContainer>
                        <ActionsContainerLeft>
                            <Button type="tertiary" scheme="outline" icon={<EyeIcon />} to={route("general:salon", salon.state.slug)}>Landing</Button>
                        </ActionsContainerLeft>
                        <Flex gap="10px">
                            <Button type="tertiary" scheme="outline" icon={<EditIcon />} to={route("admin:salons:edit", id)}>Edit</Button>
                            <Button type="tertiary" scheme="outline" icon={<DeleteIcon />} onClick={deleteSalon}>Delete</Button>
                        </Flex>
                    </ActionsContainer>
                    <NavigationLowWrapper>
                        <NavigationLow padding="0 30px">
                            <NavigationLow.Item label="Dashboard" to={route("admin:salon", id)} />
                            <NavigationLow.Item label="Customers" to={route("admin:salon:customers", id)} />
                            <NavigationLow.Item label="Gallery" to={route("admin:salon:gallery", id)} />
                            <NavigationLow.Item label="Juries" to={route("admin:salon:juries", id)} />
                            <NavigationLow.Item label="Payments" to={route("admin:salon:payments", id)} />
                            <NavigationLow.Item label="Promocodes" to={route("admin:salon:promocodes", id)} />
                            <NavigationLow.Item label="Groups" to={route("admin:salon:clubs", id)} />
                            <NavigationLow.Item label="Conditions" to={route("admin:salon:conditions", id)} />
                            {salon.is.finished() && <NavigationLow.Item label="Tracking" to={route("admin:salon:tracking", id)} />}
                        </NavigationLow>
                    </NavigationLowWrapper>
                    <div>
                        {children}
                    </div>
                </Grid>
            </MainContainer>
        </SalonManagerContext.Provider>
    )
}

// STYLES
const NavigationLowWrapper = styled.div`
    margin: 0 -${grid.margins.phone};
`

const ActionsContainer = styled.div`
    ${tablet(css`
        display: flex;
        justify-content: space-between;
    `)}
`

const ActionsContainerLeft = styled.div`
    margin-bottom: 10px;

    ${tablet(css`
        margin-bottom: 0;
        margin-right: 10px;
    `)}
`